<template>
  <div class="borrow">
    <div class="box box-primary">
      <div class="box-header with-border">
        <h3 class="box-title">
          {{ $t('menu.applicant') }}
        </h3>
        <div class="pull-right box-tools d-sm-none d-md-block">
          <template v-if="can([Permissions.backend.applicant.filterColumnTable])">
            <router-link
              :to="{name: 'switch-table-column-applicant'}"
              class="btn btn-default btn-sm margin-right-5"
            >
              <i class="fa fa-columns" /> {{ $t('label.showOrHideColumns') }}
            </router-link>
          </template>
          <template v-if="can([Permissions.backend.applicant.export])">
            <a
              :href="`${baseUrl}/export/applicant/pending`"
              class="btn btn-info btn-sm margin-right-5"
            >
              <i class="fa fa-file-excel-o" /> {{ $t('label.exportAsExcel') }}
            </a>
          </template>
        </div>
      </div>
      <div class="box-body">
        <div class="row">
          <div class="dataTables_row_up">
            <div class="col-xs-8 col-sm-6 margin-bottom">
              <div class="d-flex">
                <div class="btn-group mr-2">
                  <button
                    type="button"
                    class="btn btn-default"
                  >
                    {{ $t('label.showOrHideColumns') }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-default dropdown-toggle"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <span class="caret" />
                    <span class="sr-only">Toggle Dropdown</span>
                  </button>
                  <ul
                    class="dropdown-menu"
                    aria-labelledby="showColumnRow"
                  >
                    <template v-for="(column, columnIndex) in columns">
                      <li
                        @click="toggleColumn(columnIndex)"
                        :key="columnIndex"
                      >
                        <a
                          href="javascript:void(0)"
                          :class="`${(!oTable || oTable.columns().visible()[columnIndex]) ? 'text-blue' : 'text-red'}`"
                        >
                          <i
                            :class="`fa fa-${(!oTable || oTable.columns().visible()[columnIndex]) ? 'eye' : 'eye-slash'}`"
                          />
                          {{ $t(column.titleKey) }}
                        </a>
                      </li>
                    </template>
                  </ul>
                </div>
                <div class="margin-right-5">
                  <date-picker
                    v-model="dateRange"
                    :placeholder="$t('placeholder.chooseDateRage')"
                    format="DD/MM/YYYY"
                    :disabled-date="notBeforeToday"
                    range
                    :lang="$datepickerlocale[$i18n.locale]['lang']"
                  />
                </div>
              </div>
            </div>
            <div class="col-xs-4 col-sm-6 margin-bottom d-md-none d-sm-block">
              <div class="pull-right box-tools">
                <a
                  :href="`${baseUrl}/export/pending`"
                  class="btn btn-info btn-sm"
                >
                  <i class="fa fa-file-excel-o" /> {{ $t('label.exportAsExcel') }}
                </a>
              </div>
            </div>
          </div>
          <div class="col-md-12">
            <div>
              <table
                id="applicant-table"
                class="table table-hover table-striped table-bordered"
              >
                <tbody />
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <show-score-description-modal />
    <approved-repeated-client-modal @refreshDatatable="refreshDatatable" />
    <applicant-cancel-modal />
    <comment-modal
      :route-view-all-comments="{
        name: 'comments-applicant',
        params: {
          borrow_id
        }
      }"
    />
    <assign-loan-officer-modal @refreshList="oTable.draw()" />
    <applicant-rejection-modal @refreshList="oTable.draw()" />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import CommentModal from '@/views/comments/components/CommentModal'
import ShowScoreDescriptionModal from '../includes/ShowScoreDescriptionModal'
import ApprovedRepeatedClientModal from '../includes/ApprovedRepeatedClientModal'
import ApplicantCancelModal from '@/views/borrows/_components/ApplicantCancelModal'
import AssignLoanOfficerModal from '@/views/borrows/_components/AssignLoanOfficerModal'
import ApplicantRejectionModal from '@/views/borrows/_components/ApplicantRejectionModal'
import Permissions from '@/permissions'

export default {
  name: 'List',
  components: {
    ApplicantRejectionModal,
    AssignLoanOfficerModal,
    CommentModal,
    ApplicantCancelModal,
    ApprovedRepeatedClientModal,
    ShowScoreDescriptionModal
  },
  metaInfo () {
    return {
      title: this.$t('menu.applicant'),
      titleTemplate: '%s | ' + process.env.VUE_APP_NAME
    }
  },
  computed: {
    ...mapState({
      borrow_id: state => state.comment.borrow_id
    }),
    ...mapGetters({
      reloadOTable: 'applicantCancel/getReloadOTable'
    }),
    startDate () {
      if (this.dateRange && this.dateRange[0]) {
        return this.$moment(this.dateRange[0]).locale('en').format('YYYY-MM-DD')
      }
      return null
    },
    endDate () {
      if (this.dateRange && this.dateRange[1]) {
        return this.$moment(this.dateRange[1]).locale('en').format('YYYY-MM-DD')
      }
      return null
    },
    columns () {
      if (this.filterColumns && Array.isArray(this.filterColumns) && this.filterColumns.length > 0) {
        return this.filterColumns.map((filterColumn) => {
          const column = JSON.parse(filterColumn.option)
          this.$set(column, 'title', this.$t(column.titleKey))
          return column
        })
      }
      return []
    }
  },
  data () {
    return {
      baseUrl: process.env.VUE_APP_API,
      filterColumns: [],
      oTable: null,
      dateRange: null,
      Permissions
    }
  },
  watch: {
    '$i18n.locale' () {
      this.oTable.destroy()
      this.oTable = this.dataTable()
      this.oTable.draw(true)
    },
    reloadOTable () {
      if (this.reloadOTable) {
        this.oTable.draw(true)
      }
    },
    dateRange () {
      this.oTable.draw()
    }
  },
  methods: {
    notBeforeToday (date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0))
    },
    refreshDatatable () {
      this.oTable.draw(true)
    },
    toggleColumn (column) {
      column = this.oTable.column(column)
      column.visible(!column.visible())
    },
    onApproved (borrowId, loanUuid) {
      this.$swal({
        title: this.$t('string.approvalApplication'),
        text: this.$t('string.doYouWantToApproveThisApplicant?'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        cancelButtonText: this.$t('string.cancel'),
        confirmButtonText: this.$t('string.yesIWant')
      }).then((result) => {
        if (result.value) {
          this.$isLoading(true)
          this.$axios.post(process.env.VUE_APP_API + '/api/v1/borrows/approved', {
            id: borrowId,
            status: 'Approved',
            loan_uuid: loanUuid
          }).then((response) => {
            if (response.data.code === 1) {
              this.oTable.draw(true)
              this.$toastr('success', this.$t('string.theRequestHaveBeenProcessed'), this.$t('string.success'))
            } else {
              if (response.data.code === 0) {
                const { data } = response.data
                if (data) {
                  const { createBorrower } = data
                  if (createBorrower && Array.isArray(createBorrower) && createBorrower.length > 0) {
                    let html = '<ul>'
                    createBorrower.forEach((message) => {
                      html += `<li>${message.toString()}</li>`
                    })
                    html += '</ul>'
                    this.$toastr('error', html, this.$t('string.error'))
                  }
                }
                if (typeof response.data.message === 'string') {
                  this.$toastr('error', response.data.message, this.$t('string.error'))
                } else {
                  if (response.data.message.length > 0) {
                    const messages = response.data.message
                    try {
                      let html = '<ul>'
                      messages.forEach((message) => {
                        html += '<li>' + message + '</li>'
                      })
                      html += '</ul>'
                      this.$toastr('warning', html, this.$t('string.loandiskIntegration'))
                    } catch (e) {
                      this.$toastr('warning', 'Problem on returning message.', this.$t('string.loandiskIntegration'))
                    }
                  }
                }
              }
            }
          }).catch((error) => {
            this.onResponseError(error)
          }).finally(() => {
            this.$isLoading(false)
          })
        }
      })
    },
    onClickDeleteRecord (recordId) {
      const self = this
      this.$swal({
        title: this.$t('string.deleteApplication'),
        text: this.$t('string.youWillNotBeRevert'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('string.yesIDeleteIt!'),
        cancelButtonText: this.$t('string.cancel')
      }).then((result) => {
        if (result.value) {
          this.$axios.post(process.env.VUE_APP_API + '/api/v1/borrows/delete', { id: recordId })
            .then((response) => {
              if (response.data.code === 1) {
                self.oTable.draw(true)
                self.$swal(
                  this.$t('string.deleted'),
                  this.$t('string.theRequestHaveBeenProceeded'),
                  'success'
                )
              } else {
                this.$toastr('warning', response.data.message, 'Delete Applicant')
              }
            })
            .catch((error) => {
              this.onResponseError(error)
            })
        }
      })
    },
    dataTable () {
      const self = this
      return $('#applicant-table').DataTable({
        stateSave: true,
        processing: true,
        serverSide: true,
        scrollX: true,
        scrollCollapse: true,
        pageLength: process.env.VUE_APP_PAGE_LENGTH,
        lengthMenu: this.$lengthMenu,
        ajax: {
          method: 'POST',
          url: process.env.VUE_APP_API + '/api/v4/backend/applicant/datatable',
          data: (d) => {
            d.status = 'Pending'
            d.labelShow = self.$t('label.show')
            d.labelEdit = self.$t('label.edit')
            d.labelApproved = self.$t('label.approved')
            d.labelRejected = self.$t('label.rejected')
            d.labelDelete = self.$t('label.delete')
            d.noPermission = self.$t('string.noPermission')
            d.assignLoanOfficerLabel = self.$t('string.assignLoanOfficer')
            d.commentLabel = self.$t('string.comment')
            d.na = self.$t('string.na')
            d.startDate = self.startDate
            d.endDate = self.endDate
          },
          error: (xhr) => {
            this.onAccessDenied(xhr)
          }
        },
        columns: self.columns,
        order: [
          [1, 'desc']
        ],
        language: {
          url: `/locale/${this.$i18n.locale}.json`
        }
      })
    },
    ...mapActions('rejectApplicant', {
      setRejectApplicant: 'setRejectApplicant'
    }),
    ...mapActions('applicantCancel', {
      set_applicable_id_product: 'set_applicable_id_product'
    }),
    setButtonAction () {
      const self = this
      this.clearEventHandlers([
        '.borrow-approved',
        '.borrow-rejected',
        '.borrow-delete',
        '.borrow-edit',
        '.borrow-show',
        '.show-score-description',
        '.btn-approved-repeated-client',
        '.borrow-cancel',
        '.btn-comment',
        '.btn-assign-loan-officer'
      ])

      $(document).on('click', '.show-score-description', function (e) {
        e.preventDefault()
        self.$axios.post(process.env.VUE_APP_API + '/api/v1/profile-scoring/show-score-description', {
          borrow_uuid: $(this).attr('data-uuid')
        }).then(async ({ data }) => {
          $('#showScoreDesc').modal('toggle')
          await self.$store.commit('profileScoring/setScoreDescription', data.data)
        })
      })
      $(document).on('click', '.btn-approved-repeated-client', function (e) {
        e.preventDefault()
        self.$store.commit('approvedRepeatedClient/setApprovedRepeatedData', {
          previous_id: $(this).attr('data-previous-id'),
          current_id: $(this).attr('data-current-id'),
          loan_uuid: $(this).attr('data-loan-uuid')
        })
      })
      $(document).on('click', '.borrow-approved', function (e) {
        e.preventDefault()
        const borrow_id = $(this).attr('data-borrow')
        const loan_uuid = $(this).attr('data-loan-uuid')
        self.onApproved(borrow_id, loan_uuid)
      })
      $(document).on('click', '.borrow-rejected', function (e) {
        e.preventDefault()
        self.setRejectApplicant({
          id: $(this).attr('data-borrow'),
          product: $(this).attr('data-product-type')
        })
      })
      $(document).on('click', '.borrow-delete', function (e) {
        e.preventDefault()
        self.onClickDeleteRecord($(this).attr('data-borrow'))
      })
      $(document).on('click', '.borrow-edit', function (e) {
        e.preventDefault()
        self.$router.push({
          name: 'edit-applicant',
          params: {
            id: $(this).attr('data-borrow')
          }
        })
      })
      $(document).on('click', '.borrow-show', function (e) {
        e.preventDefault()
        self.$router.push({
          name: 'show-applicant',
          params: {
            id: $(this).attr('data-borrow')
          }
        })
      })
      $(document).on('click', '.borrow-cancel', function (e) {
        e.preventDefault()
        self.set_applicable_id_product({
          applicantSelected: $(this).attr('data-borrow'),
          product: $(this).attr('data-product-type')
        })
      })

      // comment feature
      $(document).on('click', '.btn-comment', function (e) {
        e.preventDefault()
        self.$store.commit('comment/openCommentModal')
        self.$store.commit('comment/setBorrowId', $(this).attr('data-borrow'))
      })
      // assign loan officer
      $(document).on('click', '.btn-assign-loan-officer', function (e) {
        e.preventDefault()
        self.$store.commit('loanOfficerAssignment/toggleAssignLoanOfficerModal')
        self.$store.commit('loanOfficerAssignment/setBorrowId', $(this).attr('data-borrow'))
      })
    },
    async getColumns () {
      if (localStorage.hasOwnProperty('application_columns')) {
        this.filterColumns = JSON.parse(localStorage.getItem('application_columns'))
        return 0
      }

      await this.$axios.post(process.env.VUE_APP_API + '/api/v4/backend/filter-table-column/get-option', {
        model_type: 'Applicant'
      }).then(({ data }) => {
        if (data.data) {
          this.filterColumns = data.data
          localStorage.setItem('application_columns', JSON.stringify(this.filterColumns))
        }
      })
    }
  },
  async mounted () {
    await this.getColumns()
      .then(() => {
        this.oTable = this.dataTable()
        this.setButtonAction()
      })
  },
  beforeDestroy () {
    if (this.oTable !== null) {
      this.oTable.clear()
      this.oTable.destroy()
    }
  }
}
</script>

<style scoped>

</style>
